import React from "react";
import { type RouteComponentProps, withRouter } from "react-router-dom";

import { faArrowAltCircleUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import uuidv4 from "uuid/v4";

import { dispatchAnalyticsTrack } from "~/lib/segment";
import { isiOSApp, isMobile, isTeams } from "~/lib/utils";
import { Button } from "~/ui";
import { HelpCenterLink } from "~/ui/legacyFellowIndex";

import { FeatureInfo, type FeatureValues } from "./utils";

import styles from "./css/PaidFeatureBlock.module.css";

interface Props extends RouteComponentProps {
	id: FeatureValues;
}

function PaidFeatureBlock({ id, history }: Props) {
	const { title, boldText, description, product, videoURL, image } = FeatureInfo[id];
	const mobileText = isiOSApp
		? "This feature is currently not available for your account."
		: // eslint-disable-next-line max-len
			`The ${product.name} plan is built for teams looking to manage all their 1-on-1s & meeting notes while fostering a culture of accountability.`;

	const analyticsProperties = React.useMemo(
		() => ({
			fingerprint: uuidv4(),
			ux: "dimmer",
			feature: id,
		}),
		[id],
	);

	React.useEffect(() => {
		dispatchAnalyticsTrack("Paywall Encountered", analyticsProperties);
	}, [analyticsProperties]);

	const onUpgradeClick = React.useCallback(() => {
		if (isTeams) {
			window.open(`${window.location.origin}/account/billing/plan/?from=upgrade`, "_blank", "noopener");
			return;
		}

		history.push("/account/billing/plan/?from=upgrade");

		dispatchAnalyticsTrack("Paywall Action Taken", { ...analyticsProperties, action: "redirect_to_billing" });
	}, [history, analyticsProperties]);

	const onLearnClick = React.useCallback(() => {
		dispatchAnalyticsTrack("Paywall Action Taken", { ...analyticsProperties, action: "redirect_to_marketing" });
	}, [analyticsProperties]);

	const enterprisePlan = product.family === "ENTERPRISE" || product.family === "ENTERPRISE_PLUS";

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<FontAwesomeIcon className={styles.crownIcon} icon={product.icon} />
			</div>

			<div className={styles.body}>
				<h3>
					<strong className="text-underlay text-underlay-orange">{title}</strong>
					{!isiOSApp && ` is ${enterprisePlan ? "an" : "a"} ${product.name} feature`}
				</h3>
				<p>
					<strong>{boldText}</strong> {description}{" "}
					<HelpCenterLink articleId={product.helpArticleId} onClick={onLearnClick}>
						Learn more
					</HelpCenterLink>
				</p>
				{videoURL && <video autoPlay loop muted src={videoURL} preload="auto" width="100%" crossOrigin="anonymous" />}
				{image && !isMobile ? image : null}
				<h6>{mobileText}</h6>
			</div>

			{!isiOSApp && (
				<div className={styles.footer}>
					<Button size="wide" variant="billing" onClick={onUpgradeClick} className={styles.upgradeButton}>
						<FontAwesomeIcon icon={faArrowAltCircleUp} />
						&nbsp; Upgrade
					</Button>
				</div>
			)}
		</div>
	);
}

export default withRouter(PaidFeatureBlock);
